// Phase SIKA-5b/3 — Plan Timeline line chart helper (Gap #8).
//
// Pure function. Builds the SVG geometry for the Plan Timeline panel
// AND the per-week stat card data used by the W01..W13 cards row.
// Vitest-pinned so chart geometry stays stable across refactors.
//
// Doctrine:
//   - Honest empty state: zero settled cycles → empty actual line +
//     projected trajectory from (1, current_amount) to (totalWeeks,
//     goal) using compounded dynamic target.
//   - No fabrication: actual amounts come from settled cycle snapshots.
//     Projected weeks compound at the dynamic target from the anchor
//     point. Projected values cap at goal.target_amount.
//   - The legacy fields `points` / `projectedPoints` / `actualPath` /
//     `projectedPath` / `xScale` / `yScale` / `onPace` / `goalLineY`
//     remain on the return type so SIKA-5 tests still pass.
//   - SIKA-5b/3 adds `weekPoints` (per-week PlanTimelinePoint for the
//     W-cards row), `fillPath` (area-under-curve), per-week projected
//     compounding, recalibrated flagging, and current-week marking.

export interface PlanTimelineCycleInput {
  cycle_number: number;
  /** Cumulative current_amount AFTER this cycle settled, in GH₵. */
  current_amount: number;
  /** Whether the cycle has settled. Building/published/locked => not settled. */
  settled: boolean;
  /** N06 — was this cycle recalibrated? Drives RECAL marker on the W-card. */
  recalibrated?: boolean;
  /** Dynamic odds target applied this week. Used for the "2.05×" card line. */
  dynamic_target?: number;
  /** Outcome of the cycle if settled. */
  outcome?: "won" | "lost" | "void" | "skipped" | null;
}

export interface PlanTimelineSvgInput {
  goal: number;
  totalWeeks: number;
  currentWeek: number;
  cycles: PlanTimelineCycleInput[];
  /** Anchor "now" amount when projecting forward from currentWeek. Falls
   *  back to last settled cycle's current_amount if not supplied. */
  currentAmount?: number;
  /** Dynamic target used to compound forward from currentWeek. Defaults
   *  to the last settled cycle's dynamic_target, then to 2.0. */
  projectionTarget?: number;
  width?: number;
  height?: number;
  paddingX?: number;
  paddingY?: number;
}

export interface PlanTimelinePoint {
  week: number;
  value: number;
  x: number;
  y: number;
}

/** Per-week point used by both the chart and the W01..W13 card row. */
export interface PlanTimelineWeekPoint {
  week: number;
  amount: number;
  target_mult: number;
  status: "completed" | "current" | "projected";
  recalibrated: boolean;
  outcome: "won" | "lost" | "void" | "skipped" | null;
  /** SVG x/y coords. */
  x: number;
  y: number;
}

export interface PlanTimelineSvg {
  width: number;
  height: number;
  paddingX: number;
  paddingY: number;
  goalLineY: number;
  /** Legacy: settled-cycle points only (for old SIKA-5 tests). */
  points: PlanTimelinePoint[];
  /** Legacy: straight-line projection from last settled to (totalWeeks, goal). */
  projectedPoints: PlanTimelinePoint[];
  /** Legacy SVG path "M ... L ..." for the settled line. */
  actualPath: string;
  /** Legacy projected path. */
  projectedPath: string;
  xScale: (week: number) => number;
  yScale: (amount: number) => number;
  onPace: boolean;

  // ─── SIKA-5b/3 additions ───────────────────────────────────────
  /** Per-week point array, length === totalWeeks. */
  weekPoints: PlanTimelineWeekPoint[];
  /** Path through completed + current week (solid stroke). */
  actualWeekPath: string;
  /** Path through current + projected weeks (dashed stroke). */
  projectedWeekPath: string;
  /** Filled area under the actual line (gold tint with low opacity). */
  fillPath: string;
}

export function buildPlanTimelineSvg(input: PlanTimelineSvgInput): PlanTimelineSvg {
  const width = input.width ?? 720;
  const height = input.height ?? 220;
  const paddingX = input.paddingX ?? 36;
  const paddingY = input.paddingY ?? 24;
  const totalWeeks = Math.max(1, input.totalWeeks);
  const goal = Math.max(1, input.goal);
  const currentWeek = Math.max(0, Math.min(totalWeeks, input.currentWeek));

  const innerW = Math.max(1, width - paddingX * 2);
  const innerH = Math.max(1, height - paddingY * 2);

  // X is parameterised over weeks 0..totalWeeks (W01 at week=1).
  const xScale = (week: number): number => {
    const t = Math.max(0, Math.min(totalWeeks, week)) / totalWeeks;
    return paddingX + t * innerW;
  };
  const yScale = (amount: number): number => {
    const t = Math.max(0, Math.min(goal, amount)) / goal;
    return paddingY + (1 - t) * innerH;
  };

  const goalLineY = yScale(goal);

  // ─── Legacy points (kept for SIKA-5 tests) ─────────────────────
  const settled = [...input.cycles]
    .filter((c) => c.settled)
    .sort((a, b) => a.cycle_number - b.cycle_number);

  const points: PlanTimelinePoint[] = settled.map((c) => ({
    week: c.cycle_number,
    value: c.current_amount,
    x: xScale(c.cycle_number),
    y: yScale(c.current_amount),
  }));

  const lastSettled = points.length > 0
    ? points[points.length - 1]!
    : { week: 0, value: 0, x: xScale(0), y: yScale(0) };

  const projectedPoints: PlanTimelinePoint[] = [
    { week: lastSettled.week, value: lastSettled.value, x: lastSettled.x, y: lastSettled.y },
    { week: totalWeeks, value: goal, x: xScale(totalWeeks), y: yScale(goal) },
  ];

  const buildPath = (pts: ReadonlyArray<{ x: number; y: number }>): string => {
    if (pts.length === 0) return "";
    return pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(" ");
  };

  const requiredAtCurrent = (currentWeek / totalWeeks) * goal;
  const onPace = lastSettled.value >= requiredAtCurrent * 0.95;

  // ─── SIKA-5b/3 per-week reconstruction ────────────────────────
  // Build a quick lookup by cycle_number from input.
  const byWeek = new Map<number, PlanTimelineCycleInput>();
  for (const c of input.cycles) byWeek.set(c.cycle_number, c);

  // Anchor: amount + target used for forward compounding.
  const lastSettledCycle = settled.length > 0 ? settled[settled.length - 1]! : null;
  const anchorAmount = input.currentAmount
    ?? (lastSettledCycle?.current_amount ?? 0);
  // Pick the most recent settled cycle's dynamic_target, else default 2.0.
  const projTarget = input.projectionTarget
    ?? (lastSettledCycle?.dynamic_target ?? 2.0);

  const weekPoints: PlanTimelineWeekPoint[] = [];
  let lastKnownAmount = 0;
  for (let w = 1; w <= totalWeeks; w += 1) {
    const cycle = byWeek.get(w);
    let amount: number;
    let status: "completed" | "current" | "projected";
    let target_mult: number;
    let recalibrated = false;
    let outcome: PlanTimelineWeekPoint["outcome"] = null;

    if (cycle && cycle.settled) {
      amount = Math.max(0, cycle.current_amount);
      status = w === currentWeek ? "current" : "completed";
      target_mult = cycle.dynamic_target ?? projTarget;
      recalibrated = cycle.recalibrated === true;
      outcome = (cycle.outcome ?? null) as PlanTimelineWeekPoint["outcome"];
      lastKnownAmount = amount;
    } else if (w === currentWeek) {
      // Current week — use anchor amount.
      amount = Math.max(lastKnownAmount, anchorAmount);
      status = "current";
      target_mult = cycle?.dynamic_target ?? projTarget;
      recalibrated = cycle?.recalibrated === true;
      outcome = (cycle?.outcome ?? null) as PlanTimelineWeekPoint["outcome"];
      lastKnownAmount = amount;
    } else if (w < currentWeek) {
      // Past week with no settled cycle row — hold previous amount.
      amount = lastKnownAmount;
      status = "completed";
      target_mult = cycle?.dynamic_target ?? projTarget;
      recalibrated = cycle?.recalibrated === true;
      outcome = (cycle?.outcome ?? null) as PlanTimelineWeekPoint["outcome"];
    } else {
      // Future projection — compound forward from lastKnownAmount.
      // If we haven't seen anything yet, anchor at currentAmount or 0.
      const base = lastKnownAmount > 0
        ? lastKnownAmount
        : Math.max(0, anchorAmount);
      // 1 step of growth: base * projTarget. But projTarget is the
      // gross return multiplier (e.g. 2.0× means double); honest
      // compounding caps at goal so the line meets the goal-line.
      const next = base * Math.max(1, projTarget);
      amount = Math.min(goal, Math.max(base, next));
      status = "projected";
      target_mult = projTarget;
      recalibrated = false;
      outcome = null;
      lastKnownAmount = amount;
    }

    weekPoints.push({
      week: w,
      amount,
      target_mult,
      status,
      recalibrated,
      outcome,
      x: xScale(w),
      y: yScale(amount),
    });
  }

  // Split into actual (completed+current) and projected for path/fill.
  const actualWeek = weekPoints.filter((p) => p.status !== "projected");
  const projectedWeek: PlanTimelineWeekPoint[] = [];
  // Include the last actual point as the start of the projected path so
  // the dashed line meets the solid line visually.
  if (actualWeek.length > 0) projectedWeek.push(actualWeek[actualWeek.length - 1]!);
  for (const p of weekPoints) if (p.status === "projected") projectedWeek.push(p);

  const actualWeekPath = buildPath(actualWeek);
  const projectedWeekPath = buildPath(projectedWeek);

  // Fill: area under the actual line down to the baseline (y = bottom).
  let fillPath = "";
  if (actualWeek.length >= 2) {
    const baselineY = paddingY + innerH; // chart bottom inside padding
    const head = actualWeek
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(" ");
    const last = actualWeek[actualWeek.length - 1]!;
    const first = actualWeek[0]!;
    fillPath = `${head} L ${last.x.toFixed(2)} ${baselineY.toFixed(2)} L ${first.x.toFixed(2)} ${baselineY.toFixed(2)} Z`;
  }

  return {
    width,
    height,
    paddingX,
    paddingY,
    goalLineY,
    points,
    projectedPoints,
    actualPath: buildPath(points),
    projectedPath: buildPath(projectedPoints),
    xScale,
    yScale,
    onPace,
    weekPoints,
    actualWeekPath,
    projectedWeekPath,
    fillPath,
  };
}

// ─────────────────────────────────────────────────────────────────
// formatCompactAmount — compact GH₵ amount string for the W-cards.
//
// Rule (matches frame_018):
//   - n < 1000          → digits, no suffix.       950   → "950"
//   - 1000 ≤ n < 100k   → 3 sig digits + "K".      3100  → "3.1K"
//                                                  80500 → "80.5K"
//   - n ≥ 100k          → first 3 digits + "...".  113200 → "113..."
//                                                  204000 → "204..."
//
// Negative inputs treated as 0. NaN/Infinity treated as 0.
// ─────────────────────────────────────────────────────────────────
export function formatCompactAmount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(Math.round(n));
  if (n < 100_000) {
    const k = n / 1000;
    // Show one decimal up to 99.9K (e.g. 80500 → "80.5K"). Drop the
    // trailing .0 for round thousands (e.g. 5000 → "5K"? frame shows
    // "5.4K" / "9.8K" so keep one decimal even for clean values).
    const rounded = Math.round(k * 10) / 10;
    return `${rounded.toFixed(1)}K`;
  }
  // ≥ 100k: show first 3 digits + ellipsis (matches "113...", "141...",
  // "175...", "195...", "204..." from frame_018).
  const head = String(Math.floor(n / 1000)).slice(0, 3);
  return `${head}...`;
}

// ─────────────────────────────────────────────────────────────────
// derivePaceState — pace pill state at top-right of Plan Timeline.
//
//   - 'recalibrating' wins if the LATEST settled cycle is recalibrated.
//   - 'ahead'         when actual > linear-required by >5%.
//   - 'behind'        when actual < linear-required by >5%.
//   - 'on_pace'       otherwise (within ±5%).
//
// "Linear-required at week W" is goal × (W / totalWeeks). currentWeek
// is clamped to [1, totalWeeks].
// ─────────────────────────────────────────────────────────────────
export type SikaPaceState = "ahead" | "on_pace" | "behind" | "recalibrating";

export interface PaceStateInput {
  current_amount: number;
  target_amount: number;
  cycles: ReadonlyArray<{
    cycle_number: number;
    recalibrated?: boolean;
    settled?: boolean;
  }>;
  currentWeek: number;
  totalWeeks: number;
}

export function derivePaceState(input: PaceStateInput): SikaPaceState {
  const totalWeeks = Math.max(1, input.totalWeeks);
  const currentWeek = Math.max(1, Math.min(totalWeeks, input.currentWeek));
  const target = Math.max(1, input.target_amount);
  const actual = Math.max(0, input.current_amount);

  // Latest-settled recalibration check.
  const settled = [...input.cycles]
    .filter((c) => c.settled === undefined || c.settled === true)
    .sort((a, b) => b.cycle_number - a.cycle_number);
  if (settled.length > 0 && settled[0]!.recalibrated === true) {
    return "recalibrating";
  }

  const required = (currentWeek / totalWeeks) * target;
  if (required <= 0) return "on_pace";
  const ratio = actual / required;
  if (ratio > 1.05) return "ahead";
  if (ratio < 0.95) return "behind";
  return "on_pace";
}
